import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { SmeSessionApi } from '../tokens/sme-session.token';
import { SmeSession, SessionRep } from '../models/sme-session.model';

/**
 * REST-only implementation of `SmeSessionApi` (EM-306).
 *
 * Calls:
 *   GET  /services/embedded/api/me/session
 *   POST /services/embedded/api/me/session/switch-rep
 *   POST /services/embedded/api/me/session/logout
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Does NOT
 * replace the OIDC flow — `AuthService` still owns login/logout/change-password.
 * Rule 07 / Rule 10 — no Mock fallback; Playwright specs intercept via `page.route()`.
 *
 * Error surfacing (Rule 09) is the caller's responsibility via
 * `extractBackendErrorMessage` — this service propagates the raw `HttpErrorResponse`.
 */
@Injectable()
export class SmeSessionRestService implements SmeSessionApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.ME_SESSION;

  private readonly sessionSig = signal<SmeSession | null>(null);

  readonly session = this.sessionSig.asReadonly();
  readonly activeRep = computed<SessionRep | null>(() => this.sessionSig()?.activeRep ?? null);
  readonly reps = computed<SessionRep[]>(() => this.sessionSig()?.reps ?? []);
  readonly roleScope = computed<string | null>(() => this.sessionSig()?.roleScope ?? null);

  getSession(): Observable<SmeSession> {
    return this.http.get<SmeSession>(this.base).pipe(tap((s) => this.sessionSig.set(s)));
  }

  switchRep(repId: number): Observable<SmeSession> {
    return this.http
      .post<SmeSession>(`${this.base}/switch-rep`, { repId })
      .pipe(tap((s) => this.sessionSig.set(s)));
  }

  activationCheck(): Observable<SmeSession> {
    return this.http
      .post<SmeSession>(`${this.base}/activation-check`, {})
      .pipe(tap((s) => this.sessionSig.set(s)));
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.base}/logout`, {}).pipe(tap(() => this.sessionSig.set(null)));
  }
}
